<template>
  <div class="inline-flex items-center gap-2">
    <!-- Preview Button -->
    <button v-if="!props.headless" type="button"
      :class="props.buttonClass || 'w-auto inline-flex items-center text-sm text-white bg-theme-secondary hover:bg-opacity-95 space-x-2 tracking-wide px-3 py-2.5 rounded leading-none disabled:opacity-50 disabled:cursor-not-allowed'"
      :style="props.buttonClass ? '' : 'min-width: 100px;'" :disabled="loadingPreview" @click="openPreview">
      <DownloadIcon class="w-4 h-4" v-if="!loadingPreview" />
      {{ loadingPreview ? 'Loading…' : (buttonLabel || 'Preview Invoice') }}
    </button>

    <!-- Preview Modal -->
    <DialogRoot v-model:open="showModal">
      <DialogPortal>
        <DialogOverlay class="fixed inset-0 bg-theme-secondary bg-opacity-40" style="z-index: 999998;" />
        <DialogContent class="bg-white rounded-xl shadow-2xl flex flex-col focus:outline-none"
          style="position: fixed; left: 50%; top: 50%; transform: translate(-50%, -50%); width: 90vw; max-width: 900px; max-height: 70vh; z-index: 999999;"
          @interact-outside.prevent>
          <VisuallyHidden>
            <DialogTitle>Export Preview</DialogTitle>
          </VisuallyHidden>

          <!-- Modal Top Bar -->
          <div class="flex items-center justify-between px-6 py-2.5 border-b border-gray-200 shrink-0">
            <h2 class="text-sm font-medium text-theme-secondary">Export Preview</h2>
            <div class="flex items-center gap-2">
              <!-- Export As dropdown -->
              <DropdownMenuRoot>
                <DropdownMenuTrigger
                  class="inline-flex items-center gap-1.5 text-xs text-theme-secondary border border-gray-200 bg-gray-50 hover:bg-theme-secondary hover:text-white hover:border-theme-secondary px-3 py-2 rounded leading-none transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-secondary">
                  <DownloadIcon class="w-3 h-3" />
                  <span>Export As</span>
                  <ChevronDown class="w-3 h-3 opacity-60" />
                </DropdownMenuTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuContent align="end" :side-offset="6"
                    class="min-w-[10rem] rounded-md bg-white border border-gray-200 shadow-lg py-1 focus:outline-none"
                    style="z-index: 1000001;">
                    <DropdownMenuItem v-for="fmt in exportFormats" :key="fmt.value"
                      class="flex items-center gap-2 px-3 py-2 text-xs text-gray-700 cursor-pointer select-none outline-none transition-colors duration-150"
                      @select="handleExport(fmt.value)">
                      <component :is="fmt.icon" class="h-3.5 w-3.5 shrink-0" />
                      {{ fmt.label }}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenuPortal>
              </DropdownMenuRoot>
              <DialogClose
                class="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
                <X class="w-5 h-5" />
              </DialogClose>
            </div>
          </div>

          <!-- Table -->
          <div class="overflow-auto p-4">
            <div class="rounded-lg bg-white">
              <hot-table v-if="previewRows.length && !loadingPreview" class="invoice-preview-table"
                :settings="hotSettings" license-key="non-commercial-and-evaluation" />
              <div v-else-if="loadingPreview"
                class="flex items-center justify-center py-16 text-theme-body-light text-sm">
                Loading preview…
              </div>
              <div v-else-if="errorMsg"
                class="flex items-center justify-center py-16 text-red-600 text-sm px-6 text-center">
                {{ errorMsg }}
              </div>
              <div v-else class="flex items-center justify-center py-16 text-theme-body-light text-sm">
                No data available.
              </div>
            </div>
          </div>
        </DialogContent>
      </DialogPortal>
    </DialogRoot>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { HotTable } from '@handsontable/vue3'
import { registerAllModules } from 'handsontable/registry'
import 'handsontable/styles/handsontable.min.css'
import { ChevronDown, FileText, FileSpreadsheet, File, X } from 'lucide-vue-next'
import axios from '@/plugins/axios'
import { DownloadIcon } from "@heroicons/vue/outline";
import {
  DialogRoot,
  DialogPortal,
  DialogOverlay,
  DialogContent,
  DialogTitle,
  DialogClose,
  VisuallyHidden,
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuPortal,
  DropdownMenuContent,
  DropdownMenuItem,
} from 'radix-vue'

registerAllModules()

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
const props = defineProps({
  orderId: { type: [String, Number], required: true },
  invoiceNumber: { type: [String, Number], default: '' },
  orderNumber: { type: [String, Number], default: '' },
  companyName: { type: String, default: '' },
  buttonLabel: { type: String, default: '' },
  buttonVariant: { type: String, default: 'default' },
  buttonClass: { type: String, default: '' },
  headless: { type: Boolean, default: false },
  open: { type: Boolean, default: false },
})

const emit = defineEmits(['update:open'])

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------
const showModal = ref(false)
const loadingPreview = ref(false)
const loadingExport = ref(false)
const errorMsg = ref('')

/** Parsed preview data */
const previewHeaders = ref([])
const previewRows = ref([])

/** Raw metadata from the last successful fetch (used for PDF summary) */
const orderMeta = ref(null)

// Handsontable settings for the preview grid fixed first row as header and the first column as row header, and the rest of the columns are stretch to fit the available width. The grid is read-only and has auto row height based on content, with a sync limit of 300 rows for performance.
const CURRENCY_COL_PATTERN = /price|total|tax|amount|subtotal|shipping|discount|paid|balance|cost|fee/i

function isCurrencyCol(header) {
  return CURRENCY_COL_PATTERN.test(header)
}

function currencyRenderer(hotInstance, TD, row, col, prop, value) {
  const n = parseFloat(value)
  TD.innerText = isNaN(n) ? (value ?? '') : `$${n.toFixed(2)}`
}

const hotSettings = computed(() => ({
  data: previewRows.value.map(row => [...row]),
  colHeaders: previewHeaders.value,
  readOnly: true,
  rowHeaders: false,
  width: '100%',
  height: 'auto',
  stretchH: 'all',
  autoColumnSize: true,
  columns: previewHeaders.value.map(h =>
    isCurrencyCol(h) ? { renderer: currencyRenderer } : {}
  ),
  autoRowSize: true,
  wordWrap: true,
  autoWrapRow: false,
  autoWrapCol: false,
  disableVisualSelection: true,
  copyPaste: false,
  fillHandle: false,
  dragToScroll: false,
  outsideClickDeselects: true,
  manualColumnResize: false,
  manualRowResize: false,
  columnSorting: false,
  contextMenu: false,
}))

const exportFormats = [
  { value: 'csv', label: 'Export CSV', icon: FileText },
  { value: 'xlsx', label: 'Export XLSX', icon: FileSpreadsheet },
  { value: 'pdf', label: 'Export PDF', icon: File },
]

// ---------------------------------------------------------------------------
// Service – fetch preview/export data
// ---------------------------------------------------------------------------
async function previewInvoiceCsvServices(orderId) {
  const res = await axios.authApi.get(`/account/order/csv-export/${orderId}`)
  return res.data
}

// ---------------------------------------------------------------------------
// Payload parser
// Primary shape: { colHeaders, data, meta, summary, has_tax }
// Fallback shapes:
//   { data: { colHeaders, data } }  — one-level envelope
//   { headers, rows } / { columns, rows }
//   { data: [ array of objects ] }
// ---------------------------------------------------------------------------
function parsePayload(raw) {
  // Resolve root: prefer the object that actually has colHeaders/headers/columns
  // so we don't mistake the `data` array for an envelope.
  let payload = raw
  if (raw && !raw.colHeaders && !raw.headers && !raw.columns) {
    // Only unwrap if the nested object looks like a proper envelope
    const inner = raw.data
    if (inner && !Array.isArray(inner) && (inner.colHeaders || inner.headers || inner.columns)) {
      payload = inner
    }
  }

  let headers = []
  let rows = []

  if (payload) {
    // colHeaders + data (array of arrays or array of objects)
    if (payload.colHeaders && payload.data) {
      headers = payload.colHeaders
      rows = normaliseRows(payload.data, headers)
    }
    // headers + rows
    else if (payload.headers && payload.rows) {
      headers = payload.headers
      rows = normaliseRows(payload.rows, headers)
    }
    // columns + rows
    else if (payload.columns && payload.rows) {
      headers = Array.isArray(payload.columns)
        ? payload.columns.map(c => (typeof c === 'string' ? c : c.title || c.data || String(c)))
        : payload.columns
      rows = normaliseRows(payload.rows, headers)
    }
    // fallback: data is array of objects
    else {
      const inner = payload.data || payload.result || payload.list || payload.items
      if (Array.isArray(inner) && inner.length && !Array.isArray(inner[0])) {
        headers = Object.keys(inner[0])
        rows = inner.map(obj => headers.map(h => obj[h] ?? ''))
      } else if (Array.isArray(payload) && payload.length) {
        headers = Object.keys(payload[0])
        rows = payload.map(obj => headers.map(h => obj[h] ?? ''))
      }
    }
  }

  // Extract metadata — supports both flat fields and the { meta, summary } shape
  const m = payload?.meta || {}
  const s = payload?.summary || {}

  // Tax: sum all tax entries if taxes is an array
  const taxTotal = Array.isArray(s.taxes)
    ? s.taxes.reduce((acc, t) => acc + (t.amount || 0), 0)
    : (s.tax ?? payload?.tax ?? null)

  // Discounts: keep as individual rows if array, else wrap single value
  const discountRows = Array.isArray(s.discounts) && s.discounts.length
    ? s.discounts
    : (s.discount ?? payload?.discount) != null
      ? [{ label: 'Discount', amount: s.discount ?? payload?.discount }]
      : []

  const meta = {
    companyName: m.company_name ?? payload?.company_name ?? null,
    orderNumber: m.order_number ?? payload?.order_number ?? null,
    orderDate: m.order_date ?? null,
    referenceNumber: m.reference_number ?? null,
    assignee: m.assignee ?? null,
    billTo: m.bill_to ?? null,   // array of strings
    shipTo: m.ship_to ?? null,   // array of strings
    subtotal: s.subtotal ?? payload?.subtotal ?? null,
    tax: taxTotal,
    taxRows: Array.isArray(s.taxes) ? s.taxes : [],
    shipping: s.shipping?.amount ?? s.shipping ?? payload?.shipping ?? null,
    shippingLabel: s.shipping?.label ?? 'Shipping',
    discountRows,
    orderTotal: s.order_total ?? payload?.order_total ?? null,
    paid: s.paid ?? payload?.paid ?? null,
    balance: s.balance ?? payload?.balance ?? null,
  }

  return { headers, rows, meta }
}

function normaliseRows(data, headers) {
  if (!Array.isArray(data) || !data.length) return []
  if (Array.isArray(data[0])) return data                           // already array of arrays
  return data.map(obj => headers.map(h => obj[h] ?? ''))          // array of objects
}

// ---------------------------------------------------------------------------
// Fetch & populate preview
// ---------------------------------------------------------------------------
async function fetchPreview() {
  loadingPreview.value = true
  errorMsg.value = ''
  try {
    const raw = await previewInvoiceCsvServices(props.orderId)
    const { headers, rows, meta } = parsePayload(raw)
    // Drop columns where every row value is null/empty string
    const colIndicesToKeep = headers
      .map((_, ci) => ci)
      .filter(ci => rows.some(row => row[ci] != null && row[ci] !== ''))
    previewHeaders.value = colIndicesToKeep.map(ci => headers[ci])
    previewRows.value = rows.map(row => colIndicesToKeep.map(ci => row[ci]))
    orderMeta.value = meta
  } catch (err) {
    errorMsg.value = err?.response?.data?.message || err.message || 'Failed to load preview data.'
  } finally {
    loadingPreview.value = false
  }
}

async function openPreview() {
  showModal.value = true
  if (!previewRows.value.length) {
    await fetchPreview()
  }
}

watch(() => props.open, (val) => {
  if (val) openPreview()
})

watch(showModal, (val) => {
  if (!val) emit('update:open', false)
})

// ---------------------------------------------------------------------------
// Export dispatcher
// ---------------------------------------------------------------------------
async function handleExport(format) {

  // Ensure we have data
  if (!previewRows.value.length) {
    loadingExport.value = true
    await fetchPreview()
    loadingExport.value = false
    if (!previewRows.value.length) return
  }

  const filename = buildFilename()

  if (format === 'csv') exportCsv(filename)
  if (format === 'xlsx') exportXlsx(filename)
  if (format === 'pdf') exportPdf(filename)
}

function buildFilename() {
  const parts = []
  if (props.companyName) parts.push(props.companyName.replace(/\s+/g, '_'))
  if (props.orderNumber) parts.push(`Order_${props.orderNumber}`)
  if (props.invoiceNumber) parts.push(`Invoice_${props.invoiceNumber}`)
  if (!parts.length) parts.push(`order_${props.orderId}`)
  return parts.join('_')
}

// ---------------------------------------------------------------------------
// CSV Export  (tab-separated, sep=\t BOM header, RFC 4180 quoting)
// ---------------------------------------------------------------------------
function escapeCell(val) {
  const s = val == null ? '' : String(val)
  // Force Excel to treat pure integer strings as text to prevent scientific notation
  if (/^\d{10,}$/.test(s)) return `="${s}"`
  // Quote if the value contains tab, newline, or double-quote
  if (/[\t\n\r"]/.test(s)) return `"${s.replace(/"/g, '""')}"`
  return s
}

function exportCsv(filename) {
  const SEP = '\t'
  const lines = []
  lines.push('sep=\t')

  // ---- Header metadata block ----
  const meta = orderMeta.value || {}
  if (meta.companyName || props.companyName) {
    lines.push(escapeCell(meta.companyName || props.companyName))
  }
  if (props.invoiceNumber) lines.push(`Invoice #:\t${escapeCell(props.invoiceNumber)}`)
  const on = meta.orderNumber || props.orderNumber
  if (on) lines.push(`Order Number:\t${escapeCell(on)}`)
  if (meta.orderDate) lines.push(`Order Date:\t${escapeCell(meta.orderDate)}`)
  if (meta.referenceNumber) lines.push(`Reference Number:\t${escapeCell(meta.referenceNumber)}`)
  if (meta.assignee) lines.push(`Assignee:\t${escapeCell(meta.assignee)}`)

  const hasBill = Array.isArray(meta.billTo) && meta.billTo.length
  const hasShip = Array.isArray(meta.shipTo) && meta.shipTo.length
  if (hasBill || hasShip) {
    lines.push('')
    const billLines = hasBill ? ['Bill To', ...meta.billTo] : []
    const shipLines = hasShip ? ['Ship To', ...meta.shipTo] : []
    const maxLen = Math.max(billLines.length, shipLines.length)
    for (let i = 0; i < maxLen; i++) {
      lines.push(`${escapeCell(billLines[i] || '')}\t${escapeCell(shipLines[i] || '')}`)
    }
  }

  lines.push('')

  // ---- Data table ----
  const csvHeaders = previewHeaders.value
  lines.push(csvHeaders.map(escapeCell).join(SEP))
  for (const row of previewRows.value) {
    lines.push(row.map((cell, ci) => escapeCell(formatCellValue(cell, csvHeaders[ci]))).join(SEP))
  }

  // ---- Summary footer ----
  const taxLines = (meta.taxRows || [])
    .filter(t => t.amount != null && t.amount !== 0)
    .map(t => [t.label || 'Tax', t.amount])

  const summaryFields = [
    ['Subtotal', meta.subtotal],
    ...taxLines,
    ...(taxLines.length === 0 && meta.tax != null && meta.tax !== 0 ? [['Tax', meta.tax]] : []),
    [meta.shippingLabel || 'Shipping', meta.shipping],
    ...(meta.discountRows || []).filter(d => d.amount != null && d.amount !== 0).map(d => [d.label || 'Discount', d.amount]),
    [],
    ['Order Total', meta.orderTotal],
    ['Paid', meta.paid],
    ['Balance', meta.balance],
  ].filter(entry => !entry.length || entry[1] != null)

  if (summaryFields.length) {
    lines.push('')
    // Right-align: pad with empty tabs so label/value land in the last two columns
    const numCols = previewHeaders.value.length
    const padCount = Math.max(0, numCols - 2)
    const pad = SEP.repeat(padCount)
    for (const entry of summaryFields) {
      if (!entry.length) { lines.push(''); continue }
      const [label, value] = entry
      lines.push(`${pad}${escapeCell(label)}\t${escapeCell(formatCurrency(value))}`)
    }
  }

  const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' })
  triggerDownload(blob, `${filename}.csv`)
}

// ---------------------------------------------------------------------------
// XLSX Export  (xlsx-js-style)
// ---------------------------------------------------------------------------
function exportXlsx(filename) {
  // Dynamic import to keep initial bundle lean
  import('xlsx-js-style').then(XLSX => {
    const headers = previewHeaders.value
    const rows = previewRows.value
    const meta = orderMeta.value || {}

    // ---- Styles ----
    const sectionLabelStyle = { font: { bold: true, color: { rgb: '000000' } } }
    const metaLabelStyle = { font: { bold: true, color: { rgb: '444444' } } }
    const metaValueStyle = { font: { color: { rgb: '000000' } } }
    const tableHeadStyle = {
      fill: { patternType: 'solid', fgColor: { rgb: '000000' } },
      font: { bold: true, color: { rgb: 'FFFFFF' } },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    }
    const dataStyle = { alignment: { wrapText: true, vertical: 'top' } }
    const summaryLabelStyle = { font: { bold: false, color: { rgb: '444444' } } }
    const summaryTotalStyle = { font: { bold: true, color: { rgb: '000000' } } }

    const cell = (v, s) => ({ v: v ?? '', t: 's', s })
    const wsData = []
    const rowHeights = []

    // ---- Header metadata block ----
    const companyLabel = meta.companyName || props.companyName
    if (companyLabel) {
      wsData.push([cell(companyLabel, { font: { bold: true, sz: 14, color: { rgb: '000000' } } })])
      rowHeights.push({ hpt: 22 })
    }
    if (props.invoiceNumber) {
      wsData.push([cell('Invoice #:', metaLabelStyle), cell(props.invoiceNumber, metaValueStyle)])
      rowHeights.push({ hpt: 16 })
    }
    const on = meta.orderNumber || props.orderNumber
    if (on) {
      wsData.push([cell('Order Number:', metaLabelStyle), cell(on, metaValueStyle)])
      rowHeights.push({ hpt: 16 })
    }
    if (meta.orderDate) {
      wsData.push([cell('Order Date:', metaLabelStyle), cell(meta.orderDate, metaValueStyle)])
      rowHeights.push({ hpt: 16 })
    }
    if (meta.referenceNumber) {
      wsData.push([cell('Reference Number:', metaLabelStyle), cell(meta.referenceNumber, metaValueStyle)])
      rowHeights.push({ hpt: 16 })
    }
    if (meta.assignee) {
      wsData.push([cell('Assignee:', metaLabelStyle), cell(meta.assignee, metaValueStyle)])
      rowHeights.push({ hpt: 16 })
    }

    // ---- Bill To / Ship To block ----
    const hasBill = Array.isArray(meta.billTo) && meta.billTo.length
    const hasShip = Array.isArray(meta.shipTo) && meta.shipTo.length
    if (hasBill || hasShip) {
      wsData.push([])
      rowHeights.push({ hpt: 8 })

      wsData.push([
        cell(hasBill ? 'Bill To' : '', sectionLabelStyle),
        cell(''),
        cell(hasShip ? 'Ship To' : '', sectionLabelStyle),
      ])
      rowHeights.push({ hpt: 16 })

      const billLines = (meta.billTo || []).map(String)
      const shipLines = (meta.shipTo || []).map(String)
      const maxLen = Math.max(billLines.length, shipLines.length)
      for (let i = 0; i < maxLen; i++) {
        wsData.push([
          cell(billLines[i] || '', metaValueStyle),
          cell(''),
          cell(shipLines[i] || '', metaValueStyle),
        ])
        rowHeights.push({ hpt: 14 })
      }
    }

    // ---- Blank spacer row ----
    wsData.push([])
    rowHeights.push({ hpt: 10 })

    // ---- Data table header row ----
    wsData.push(headers.map(h => cell(h, tableHeadStyle)))
    rowHeights.push({ hpt: 24 })

    // ---- Data rows ----
    for (const row of rows) {
      wsData.push(row.map((c, ci) => {
        const formatted = formatCellValue(c, headers[ci])
        const isCurr = isCurrencyCol(headers[ci])
        return cell(formatted, { ...dataStyle, ...(isCurr ? { alignment: { horizontal: 'right', vertical: 'top' } } : {}) })
      }))
      const maxLines = Math.max(...row.map(c => String(c ?? '').split('\n').length))
      rowHeights.push({ hpt: Math.max(20, maxLines * 14) })
    }

    // ---- Summary footer ----
    const taxLines = (meta.taxRows || [])
      .filter(t => t.amount != null)
      .map(t => [t.label || 'Tax', t.amount])

    const summaryFields = [
      ['Subtotal', meta.subtotal],
      ...taxLines,
      ...(taxLines.length === 0 && meta.tax != null && meta.tax !== 0 ? [['Tax', meta.tax]] : []),
      [meta.shippingLabel || 'Shipping', meta.shipping],
      ...(meta.discountRows || []).filter(d => d.amount != null && d.amount !== 0).map(d => [d.label || 'Discount', d.amount]),
      [],
      ['Order Total', meta.orderTotal],
      ['Paid', meta.paid],
      ['Balance', meta.balance],
    ].filter(([, v]) => v != null)

    if (summaryFields.length) {
      wsData.push([])
      rowHeights.push({ hpt: 10 })

      // Right-align: summary label/value go in the last two columns of the data table
      const labelCol = Math.max(0, headers.length - 2)
      const valueCol = Math.max(1, headers.length - 1)

      for (const entry of summaryFields) {
        // blank separator row
        if (!entry.length) {
          wsData.push([])
          rowHeights.push({ hpt: 8 })
          continue
        }
        const [label, value] = entry
        const isTotal = label === 'Order Total' || label === 'Balance'
        const st = isTotal ? summaryTotalStyle : summaryLabelStyle
        const rowArr = Array.from({ length: valueCol + 1 }, () => cell('', {}))
        rowArr[labelCol] = cell(label, st)
        rowArr[valueCol] = cell(formatCurrency(value), { ...st, alignment: { horizontal: 'right' } })
        wsData.push(rowArr)
        rowHeights.push({ hpt: 16 })
      }
    }

    const ws = XLSX.utils.aoa_to_sheet(wsData)

    // Auto column widths (based on data table columns, at minimum)
    const numCols = Math.max(headers.length, 3)
    const colWidths = Array.from({ length: numCols }, (_, ci) => {
      const maxLen = wsData.reduce((acc, row) => {
        const v = row[ci]?.v ?? ''
        return Math.max(acc, String(v).length)
      }, 0)
      return { wch: Math.min(Math.max(maxLen + 2, 10), 50) }
    })
    ws['!cols'] = colWidths
    ws['!rows'] = rowHeights

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Invoice')
    XLSX.writeFile(wb, `${filename}.xlsx`)
  })
}

// ---------------------------------------------------------------------------
// PDF Export  (jspdf + jspdf-autotable)
// ---------------------------------------------------------------------------
function exportPdf(filename) {
  Promise.all([
    import('jspdf/dist/jspdf.umd.min.js'),
    import('jspdf-autotable'),
  ]).then(([jspdfMod, autoTableMod]) => {
    const jsPDF = jspdfMod.jsPDF || jspdfMod.default?.jsPDF || jspdfMod.default
    const autoTable = autoTableMod.default || autoTableMod

    const MARGIN = 36
    const FONT_SIZE = 8
    const CELL_PADDING = 8
    const A4_LANDSCAPE_W = 841.89
    const A4_LANDSCAPE_H = 595.28

    // Measure natural column widths at full font size before creating the doc
    const headers = previewHeaders.value
    const rows = previewRows.value
    const formattedRows = rows.map(row =>
      row.map((c, ci) => formatCellValue(c, headers[ci]))
    )
    const maxCharsPerCol = headers.map((h, ci) =>
      Math.max(h.length, ...formattedRows.map(r => String(r[ci] ?? '').length))
    )
    const naturalWidths = maxCharsPerCol.map(n => Math.ceil(n * FONT_SIZE * 0.55) + CELL_PADDING)
    const measuredTableW = naturalWidths.reduce((a, b) => a + b, 0)
    const pageWidth = Math.max(A4_LANDSCAPE_W, measuredTableW + MARGIN * 2)

    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: [pageWidth, A4_LANDSCAPE_H] })
    const COL_W = pageWidth - MARGIN * 2

    // Scale up column widths to fill full page width if there is leftover space
    const scale = COL_W / measuredTableW
    const colWidths = naturalWidths.map(w => w * scale)

    let cursorY = MARGIN

    // ---- Company / Order metadata block ----
    const meta = orderMeta.value || {}

    // ---- Company name ----
    doc.setFontSize(16)
    doc.setFont('helvetica', 'bold')
    doc.text(meta.companyName || props.companyName || 'Invoice', MARGIN, cursorY)
    cursorY += 18

    // ---- Order metadata line ----
    doc.setFontSize(9)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(100)

    const metaLines = []
    if (props.invoiceNumber) metaLines.push(`Invoice #: ${props.invoiceNumber}`)
    const on = meta.orderNumber || props.orderNumber
    if (on) metaLines.push(`Order Number: ${on}`)
    if (meta.orderDate) metaLines.push(`Date: ${meta.orderDate}`)
    if (meta.referenceNumber) metaLines.push(`Reference Number: ${meta.referenceNumber}`)
    if (meta.assignee) metaLines.push(`Assignee: ${meta.assignee}`)

    metaLines.forEach(line => { doc.text(line, MARGIN, cursorY); cursorY += 12 })

    // ---- Bill-to / Ship-to block ----
    const hasBill = Array.isArray(meta.billTo) && meta.billTo.length
    const hasShip = Array.isArray(meta.shipTo) && meta.shipTo.length
    if (hasBill || hasShip) {
      cursorY += 6
      const halfW = COL_W / 2
      let billY = cursorY
      let shipY = cursorY

      if (hasBill) {
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(0)
        doc.text('Bill To', MARGIN, billY)
        billY += 11
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(100)
        meta.billTo.forEach(l => { doc.text(String(l), MARGIN, billY); billY += 11 })
      }

      if (hasShip) {
        const shipX = MARGIN + halfW
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(0)
        doc.text('Ship To', shipX, shipY)
        shipY += 11
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(100)
        meta.shipTo.forEach(l => { doc.text(String(l), shipX, shipY); shipY += 11 })
      }

      cursorY = Math.max(billY, shipY) + 8
    }

    doc.setTextColor(0)

    // ---- Line-item table ----
    autoTable(doc, {
      head: [headers],
      body: formattedRows,
      startY: cursorY,
      margin: { left: MARGIN, right: MARGIN },
      tableWidth: 'auto',
      columnStyles: Object.fromEntries(colWidths.map((w, i) => [i, { cellWidth: w }])),
      headStyles: {
        fillColor: [0, 0, 0],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: FONT_SIZE,
        cellPadding: { top: 4, bottom: 4, left: 4, right: 4 },
        lineColor: [255, 255, 255],
        lineWidth: 0.3,
      },
      bodyStyles: {
        fontSize: FONT_SIZE,
        textColor: [40, 40, 40],
        fillColor: [255, 255, 255],
        minCellHeight: 0,
        cellPadding: { top: 3, bottom: 3, left: 4, right: 4 },
        overflow: 'linebreak',
        valign: 'middle',
        lineColor: [200, 200, 200],
        lineWidth: 0.3,
      },
      alternateRowStyles: { fillColor: [255, 255, 255] },
      didDrawPage(data) { cursorY = data.cursor.y },
    })

    cursorY = doc.lastAutoTable.finalY + 16

    // ---- Summary box ----
    // Expand individual tax rows if present
    const taxLines = (meta.taxRows || [])
      .filter(t => t.amount != null)
      .map(t => [t.label || 'Tax', t.amount])

    const summaryFields = [
      ['Subtotal', meta.subtotal],
      ...taxLines,
      ...(taxLines.length === 0 && meta.tax != null && meta.tax !== 0 ? [['Tax', meta.tax]] : []),
      [meta.shippingLabel || 'Shipping', meta.shipping],
      ...(meta.discountRows || []).filter(d => d.amount != null && d.amount !== 0).map(d => [d.label || 'Discount', d.amount]),
      ['Order Total', meta.orderTotal],
      ['Paid', meta.paid],
      ['Balance', meta.balance],
    ].filter(([, v]) => v != null)

    if (summaryFields.length) {
      const BOX_W = 220
      const ROW_H = 16
      const BOX_H = summaryFields.length * ROW_H + 8
      const BOX_X = pageWidth - MARGIN - BOX_W
      const BOX_Y = cursorY

      doc.setDrawColor(200)
      doc.setFillColor(248, 248, 248)
      doc.roundedRect(BOX_X, BOX_Y, BOX_W, BOX_H, 4, 4, 'FD')

      doc.setFontSize(8)
      summaryFields.forEach(([label, value], idx) => {
        const y = BOX_Y + 12 + idx * ROW_H
        const isTotal = label === 'Order Total'
        const isBalance = label === 'Balance'

        if (isTotal || isBalance) {
          doc.setFont('helvetica', 'bold')
          doc.setTextColor(0)
        } else {
          doc.setFont('helvetica', 'normal')
          doc.setTextColor(80)
        }

        doc.text(label, BOX_X + 8, y)
        doc.text(formatCurrency(value), BOX_X + BOX_W - 8, y, { align: 'right' })
      })
    }

    doc.save(`${filename}.pdf`)
  })
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCellValue(val, header) {
  if (isCurrencyCol(header)) {
    const n = parseFloat(val)
    if (!isNaN(n)) return `$${n.toFixed(2)}`
  }
  return val == null ? '' : String(val)
}

function formatCurrency(val) {
  const n = parseFloat(val)
  if (isNaN(n)) return String(val ?? '')
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n)
}

function triggerDownload(blob, name) {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', name)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

</script>

<style scoped>
/* Ensure Handsontable fills the modal grid area */
:deep(.invoice-preview-table .handsontable) {
  font-size: 12px;
  color: #111827;
}

:deep(.invoice-preview-table .htCore thead th) {
  background-color: #f3f4f6 !important;
  color: #111827 !important;
  border-color: #e5e7eb !important;
  text-align: left !important;
}

:deep(.invoice-preview-table .htCore tbody td) {
  background-color: #ffffff !important;
  color: #111827 !important;
  border-color: #e5e7eb !important;
}

:deep(.invoice-preview-table .wtHolder),
:deep(.invoice-preview-table .wtHider),
:deep(.invoice-preview-table .wtSpreader) {
  background: #ffffff !important;
}

:deep(.invoice-preview-table .area),
:deep(.invoice-preview-table .area-1),
:deep(.invoice-preview-table .area-2),
:deep(.invoice-preview-table .area-3),
:deep(.invoice-preview-table .area-4),
:deep(.invoice-preview-table .current),
:deep(.invoice-preview-table .currentRow),
:deep(.invoice-preview-table .currentCol) {
  background: transparent !important;
}

:deep(.invoice-preview-table .handsontableInput) {
  display: none !important;
}
</style>

<style>
/* Radix DropdownMenu portal renders outside scoped DOM — global rule needed */
[data-radix-popper-content-wrapper] [role="menuitem"][data-highlighted] {
  background-color: #f3f4f6;
  color: #2B303F;
  outline: none;
}
</style>
